/**
 * Phase 3 — Finalize (iter84.56.7, split out of RunbookPhaseCards.jsx at
 * iter86.83). Includes the iter86.33 CanonMergeRow (dev → main GitOps
 * merge capture).
 */
import { useState } from "react";
import {
    Send, Save, Clock, GitCommit, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function FinalizeRunbookCard({ promotion, onUpdated, environment }) {
    const [busy, setBusy] = useState(false);
    const isProd = environment === "production";

    if (isProd) return null; // finalize happens on preview after prod verification

    const noPromotion = !promotion;
    const isFinalized = promotion?.status === "finalized";
    const armed = !!promotion?.armed_at;
    const readyToFinalize = !!promotion?.ready_to_finalize;
    const postPending = promotion?.summary?.post_deploy?.required_pending || 0;
    const emergentReady =
        !!promotion?.emergent_deployment_id || !!promotion?.emergent_deployment_id_waived;
    // iter86.33 — dev-branch GitOps: PROMOTE = merge dev → main; the merge
    // identifier must be recorded (or waived) before finalize when active.
    const canonRequired = !!promotion?.canon_merge_required;
    const canonReady = !canonRequired
        || !!promotion?.canon_merge_ref || !!promotion?.canon_merge_ref_waived;

    const finalize = async () => {
        if (!promotion || !readyToFinalize) {
            toast.error("Tick all required post-deploy checks first");
            return;
        }
        if (!emergentReady) {
            toast.error("Capture the Emergent deployment ID in Phase 2 first");
            return;
        }
        if (!canonReady) {
            toast.error("Record the dev → main canon merge (PR # / merge SHA) first");
            return;
        }
        const conf = window.prompt('Type PROMOTE to finalize this promotion');
        if (conf !== "PROMOTE") return;
        setBusy(true);
        try {
            const r = await api.runbookFinalize(promotion.id, conf);
            toast.success("Promotion finalized — archived to GitHub");
            onUpdated?.(r);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Finalize failed");
        } finally {
            setBusy(false);
        }
    };

    const phaseTw = isFinalized
        ? "border-2 border-emerald-500/40 bg-emerald-500/[0.03]"
        : readyToFinalize
            ? "border-2 border-cyber/40 bg-cyber/[0.03]"
            : "border-2 border-zinc-800";

    return (
        <div className={`brutal-card p-3 space-y-2 ${phaseTw}`} data-testid="phase-3-finalize-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Send className={`w-5 h-5 ${isFinalized ? "text-emerald-400" : readyToFinalize ? "text-cyber" : "text-zinc-500"}`} />
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                            Phase 3 · Finalize
                        </p>
                        <p className="text-xs text-zinc-200 font-bold">
                            {isFinalized
                                ? "Promotion finalized — archived to GitHub"
                                : readyToFinalize
                                    ? "All post-deploy gates clear — type PROMOTE to finalize"
                                    : noPromotion
                                        ? "Start a promotion in Phase 0 first"
                                        : !armed
                                            ? "ARM the promotion in Phase 2 first"
                                            : `${postPending} required post-deploy gate(s) still pending`}
                        </p>
                    </div>
                </div>
                {!isFinalized && (
                    <button
                        type="button"
                        onClick={finalize}
                        disabled={busy || !readyToFinalize || !emergentReady || !canonReady || noPromotion}
                        data-testid="phase-3-finalize-btn"
                        className={`brutal-btn text-xs py-2 px-3 disabled:opacity-50 ${
                            readyToFinalize ? "border-cyber text-cyber" : ""
                        }`}
                        title={
                            !readyToFinalize
                                ? "Approve or Waive all required post-deploy gates"
                                : !emergentReady
                                    ? "Capture the Emergent deployment ID in Phase 2"
                                    : !canonReady
                                        ? "Record the dev → main canon merge below first"
                                        : "Type PROMOTE to confirm"
                        }
                    >
                        <Send className="w-3 h-3" /> FINALIZE — Type PROMOTE
                    </button>
                )}
                {isFinalized && (
                    <span className="pill text-[10px] font-bold uppercase border bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                        FINALIZED
                    </span>
                )}
            </div>
            {!isFinalized && armed && canonRequired && (
                <CanonMergeRow promotion={promotion} onUpdated={onUpdated} />
            )}
            {!isFinalized && armed && !emergentReady && (
                <p className="text-[11px] text-amber-300 italic flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    Capture the Emergent deployment ID in Phase 2 before finalizing.
                </p>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// iter86.33 — Canon merge capture (dev-branch GitOps). PROMOTE = merge
// dev → main in GitHub; the operator records the PR # / merge commit SHA
// here (or waives with a reason) before the finalize button unlocks.
// ---------------------------------------------------------------------------
function CanonMergeRow({ promotion, onUpdated }) {
    const captured = promotion.canon_merge_ref;
    const waived = promotion.canon_merge_ref_waived;
    const [editing, setEditing] = useState(!captured && !waived);
    const [waiving, setWaiving] = useState(false);
    const [value, setValue] = useState(captured || "");
    const [waiverReason, setWaiverReason] = useState(promotion.canon_merge_waiver_reason || "");
    const [saving, setSaving] = useState(false);

    const submit = async (body, okMsg) => {
        setSaving(true);
        try {
            const fresh = await api.runbookSetCanonMerge(promotion.id, body);
            toast.success(okMsg);
            setEditing(false);
            setWaiving(false);
            onUpdated?.(fresh);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!editing && !waiving) {
        return (
            <div
                data-testid="canon-merge-display"
                data-status={captured ? "captured" : "waived"}
                className={`px-2 py-1.5 border rounded text-[11px] flex items-center gap-2 ${
                    captured
                        ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-200"
                        : "border-amber-500/40 bg-amber-500/5 text-amber-200"
                }`}
            >
                {captured
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-75">
                        Canon merge · dev → main
                    </p>
                    <p className="font-mono text-[11px] truncate" title={captured || "Waived"}>
                        {captured || "Waived"}
                    </p>
                    {waived && promotion.canon_merge_waiver_reason && (
                        <p className="text-[10px] italic opacity-75 truncate" title={promotion.canon_merge_waiver_reason}>
                            "{promotion.canon_merge_waiver_reason}"
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => { setValue(captured || ""); setEditing(true); }}
                    disabled={saving}
                    data-testid="canon-merge-edit-btn"
                    className="text-[10px] underline opacity-75 hover:opacity-100 shrink-0"
                >
                    Edit
                </button>
            </div>
        );
    }

    if (waiving) {
        return (
            <div data-testid="canon-merge-waiving" className="px-2 py-2 border border-amber-500/40 bg-amber-500/5 rounded text-[11px]">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300 mb-1">
                            Waive canon merge capture
                        </p>
                        <input
                            type="text"
                            value={waiverReason}
                            onChange={(e) => setWaiverReason(e.target.value)}
                            placeholder="Why are you skipping the dev → main merge record?"
                            data-testid="canon-merge-waiver-reason"
                            className="brutal-input w-full text-[11px]"
                            autoFocus
                        />
                        <div className="flex items-center gap-2 mt-2">
                            <button
                                type="button"
                                onClick={() => submit({ waive: true, waiver_reason: waiverReason.trim() }, "Canon merge waived")}
                                disabled={saving || !waiverReason.trim()}
                                data-testid="canon-merge-waive-confirm"
                                className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-50"
                            >
                                {saving ? "Waiving…" : "Confirm waive"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setWaiving(false)}
                                disabled={saving}
                                data-testid="canon-merge-waive-cancel"
                                className="text-[10px] text-zinc-400 hover:text-white underline"
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="canon-merge-editing" className="px-2 py-2 border border-volt/40 bg-volt/5 rounded text-[11px]">
            <div className="flex items-start gap-2">
                <GitCommit className="w-4 h-4 text-volt mt-0.5 shrink-0" />
                <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-volt mb-1">
                        Record the dev → main canon merge
                    </p>
                    <p className="text-[10px] text-zinc-400 mb-2 leading-relaxed">
                        PROMOTE = merge the verified <code>dev</code> branch into <code>main</code> in GitHub
                        (Pull requests → New PR → base <code>main</code> ← compare <code>dev</code> → Merge).
                        Paste the PR # and/or merge commit SHA here — required before finalize.
                    </p>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="e.g. PR #12 · merge a1b2c3d"
                        data-testid="canon-merge-input"
                        className="brutal-input w-full text-[11px] font-mono"
                        /* no autoFocus — this row mounts at ARM time together with
                           the Phase-2 deployment-ID input; that one should win focus
                           (deploy comes before the canon merge). iter86.35 */
                    />
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => submit({ merge_ref: value.trim() }, "Canon merge recorded")}
                            disabled={saving || !value.trim()}
                            data-testid="canon-merge-save-btn"
                            className="brutal-btn text-[10px] py-1 px-2 disabled:opacity-50"
                        >
                            <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
                        </button>
                        {(captured || waived) && (
                            <button
                                type="button"
                                onClick={() => { setEditing(false); setValue(captured || ""); }}
                                disabled={saving}
                                data-testid="canon-merge-cancel-edit"
                                className="text-[10px] text-zinc-400 hover:text-white underline"
                            >
                                Cancel
                            </button>
                        )}
                        <span className="text-zinc-600 text-[10px]">·</span>
                        <button
                            type="button"
                            onClick={() => setWaiving(true)}
                            disabled={saving}
                            data-testid="canon-merge-waive-btn"
                            className="text-[10px] text-amber-300 hover:text-amber-200 underline"
                        >
                            Waive (skip with reason)
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
